import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { CoachProfileSchema } from "./coach.js";
import { WorkforceInvitationIssueResultSchema } from "./workforce-invitation.js";

const CanonicalCoachOnboardingIdSchema = Type.String({
  pattern: "^coach_[a-f0-9]{32}$",
});
const CanonicalCoachOnboardingIdempotencyKeySchema = Type.String({
  minLength: 8,
  maxLength: 96,
});
const CanonicalCoachOnboardingReasonSchema = Type.String({ minLength: 2, maxLength: 500 });

export const CANONICAL_COACH_ONBOARDING_PROFILE_IDEMPOTENCY_PREFIX = "coach-onboard-profile:";
export const CANONICAL_COACH_ONBOARDING_INVITATION_IDEMPOTENCY_PREFIX = "coach-onboard-invitation:";

export function canonicalCoachOnboardingProfileIdempotencyKey(rootKey: string): string {
  return `${CANONICAL_COACH_ONBOARDING_PROFILE_IDEMPOTENCY_PREFIX}${rootKey}`;
}

export function canonicalCoachOnboardingInvitationIdempotencyKey(rootKey: string): string {
  return `${CANONICAL_COACH_ONBOARDING_INVITATION_IDEMPOTENCY_PREFIX}${rootKey}`;
}

export function canonicalCoachOnboardingReasonIsSafe(reason: string): boolean {
  return !/(?:password|token|secret|credential|otp|验证码|密码|口令|令牌|密钥)/iu.test(reason);
}

/**
 * Public owner command for creating one coach profile and issuing the coach's
 * own activation invitation. The browser cannot submit roles, workforce ids,
 * configuration collections, credentials or authoritative versions other than
 * the coach configuration baseline it just read.
 */
export const CreateCanonicalCoachOnboardingCommandSchema = Type.Object(
  {
    profileId: CanonicalCoachOnboardingIdSchema,
    coachNumber: Type.String({
      minLength: 1,
      maxLength: 32,
      pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
    }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    controlledPhone: Type.String({
      minLength: 6,
      maxLength: 32,
      pattern: "^[0-9+\\s()（）._:/／-]+$",
    }),
    avatarUrl: Type.Union([Type.Null(), Type.String({ minLength: 1, maxLength: 500 })]),
    levelId: Type.String({ minLength: 1, maxLength: 64 }),
    expectedCoachConfigurationVersion: Type.Integer({ minimum: 1 }),
    reason: CanonicalCoachOnboardingReasonSchema,
    idempotencyKey: CanonicalCoachOnboardingIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreateCanonicalCoachOnboardingCommand = Static<
  typeof CreateCanonicalCoachOnboardingCommandSchema
>;

const [
  SimulatedWorkforceInvitationIssueResultSchema,
  ExternalWorkforceInvitationIssueResultSchema,
] = WorkforceInvitationIssueResultSchema.anyOf;
const CanonicalCoachOnboardingInvitationResultSchema = Type.Union([
  Type.Object(
    {
      ...SimulatedWorkforceInvitationIssueResultSchema.properties,
      purpose: Type.Literal("ACCOUNT_ACTIVATION"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ExternalWorkforceInvitationIssueResultSchema.properties,
      purpose: Type.Literal("ACCOUNT_ACTIVATION"),
    },
    { additionalProperties: false },
  ),
]);

export const CanonicalCoachOnboardingResultSchema = Type.Object(
  {
    idempotencyKey: CanonicalCoachOnboardingIdempotencyKeySchema,
    profile: Type.Object(
      {
        ...CoachProfileSchema.properties,
        id: CanonicalCoachOnboardingIdSchema,
        status: Type.Literal("ENABLED"),
        version: Type.Literal(1),
      },
      { additionalProperties: false },
    ),
    coachConfigurationVersion: Type.Integer({ minimum: 1 }),
    invitation: CanonicalCoachOnboardingInvitationResultSchema,
    completedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type CanonicalCoachOnboardingResult = Static<typeof CanonicalCoachOnboardingResultSchema>;

export function isCreateCanonicalCoachOnboardingCommand(
  value: unknown,
): value is CreateCanonicalCoachOnboardingCommand {
  return (
    Value.Check(CreateCanonicalCoachOnboardingCommandSchema, value) &&
    canonicalCoachOnboardingReasonIsSafe(value.reason)
  );
}

export function isCanonicalCoachOnboardingResult(
  value: unknown,
): value is CanonicalCoachOnboardingResult {
  if (!Value.Check(CanonicalCoachOnboardingResultSchema, value)) return false;
  return (
    value.invitation.roles.length === 1 &&
    value.invitation.roles[0] === "COACH" &&
    value.invitation.displayName === value.profile.displayName &&
    value.invitation.idempotencyKey ===
      canonicalCoachOnboardingInvitationIdempotencyKey(value.idempotencyKey)
  );
}
